import { reportingCall } from '../reporting/ReportingService'
import type { PatientService } from './PatientService'
import type { Patient, PatientPayload } from '../../model/patient'
import { type FirestoreService } from '../http/FirestoreService'
import { normalizeNationality } from '../../utils/countryUtils'

export class PatientServiceImpl implements PatientService {
  private readonly firestoreService: FirestoreService
  private static readonly collectionName = 'patients'

  constructor(firestoreService: FirestoreService) {
    this.firestoreService = firestoreService
  }

  async createPatient({
    attachments,
    captures,
    ...patient
  }: PatientPayload): Promise<Patient> {
    const attachmentIds: string[] = []
    if (attachments) {
      attachmentIds.push(
        ...(await this.firestoreService.uploadFiles(
          patient.ownerId,
          attachments
        ))
      )
    }

    if (captures) {
      attachmentIds?.push(
        ...(await this.firestoreService.uploadImages(patient.ownerId, captures))
      )
    }

    const newPatient: Patient = await this.firestoreService.createDocument(
      PatientServiceImpl.collectionName,
      {
        ...patient,
        nationality: normalizeNationality(patient.nationality, 'EG'),
        attachmentIds,
        statisticsMode: 'server-v1',
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
    )

    return newPatient
  }

  async updatePatient({
    attachments,
    captures,
    ...patient
  }: Patient & {
    attachments: File[]
    captures: string[]
  }): Promise<Patient> {
    const attachmentIds: string[] = [...patient.attachmentIds]
    if (attachments) {
      attachmentIds.push(
        ...(await this.firestoreService.uploadFiles(
          patient.ownerId,
          attachments
        ))
      )
    }

    if (captures) {
      attachmentIds.push(
        ...(await this.firestoreService.uploadImages(patient.ownerId, captures))
      )
    }

    return await this.firestoreService.updateDocument(
      PatientServiceImpl.collectionName,
      patient.id,
      {
        ...patient,
        nationality: normalizeNationality(patient.nationality, 'EG'),
        attachmentIds,
        updatedAt: Date.now()
      }
    )
  }

  async getPatients({ ownerId }: { ownerId: string }): Promise<Patient[]> {
    return await this.firestoreService.getDocuments(
      PatientServiceImpl.collectionName,
      ownerId
    )
  }

  async getOnePatient({
    patientId
  }: {
    patientId: string
  }): Promise<Patient | undefined> {
    const patient = await this.firestoreService.getDocument<Patient>(
      PatientServiceImpl.collectionName,
      patientId
    )
    return patient ? { ...patient, id: patientId } : undefined
  }

  async getLatestPatients({
    ownerId
  }: {
    ownerId: string
  }): Promise<Patient[]> {
    return await this.firestoreService.getDocuments(
      PatientServiceImpl.collectionName,
      ownerId,
      'updatedAt',
      'desc',
      5
    )
  }

  async searchPatients({
    searchTerm,
    ownerId
  }: {
    searchTerm: string
    ownerId: string
  }): Promise<Patient[]> {
    return await this.firestoreService.searchDocumentsByTerm(
      PatientServiceImpl.collectionName,
      ownerId,
      'name',
      searchTerm
    )
  }

  async getPatientAttachments(attachmentIds: string[]): Promise<string[]> {
    return await this.firestoreService.retrieveImages(attachmentIds)
  }

  async deletePatient(id: string): Promise<void> {
    await reportingCall('archivePatient', { patientId: id })
  }
}
